import { useEffect, useState } from 'react';
import { KasPromoCodeFields } from '../../components/KasPromoCodeFields';
import { toKasFormValue, toKasPayload } from '../../components/kasPromoCode';
import { nhomNghin } from '../../format';
import { ImageUploadField } from '../../components/ImageUploadField';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Steps,
  Switch,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { Branch, Chain } from '../catalog/api';
import {
  KIND_OPTIONS,
  REDEMPTION_WINDOW_LABELS,
  WEEKDAY_OPTIONS,
  rewardGroupOf,
  stackingIsIneffective,
  type Reward,
  type RewardKind,
  type UpsertRewardPayload,
  type VoucherConditions,
  type Weekday,
} from './api';

interface FormValues {
  name: string;
  description?: string;
  kind: RewardKind;
  pointsCost: number;
  discountAmount?: number;
  discountPercent?: number;
  maxDiscountAmount?: number;
  itemValue?: number;
  validDays: number;
  stockRemaining?: number | null;
  chainId?: string | null;
  /** `Record<chainId, code>` — mảng gửi lên do `toKasPayload` dựng theo phạm vi hiện tại. */
  kasPromoCodes?: Record<string, string>;
  isActive: boolean;
  maxRedemptionsPerMember?: number;
  redemptionWindow: 'Month' | 'Lifetime';
  saleFrom?: Dayjs | null;
  saleUntil?: Dayjs | null;
  imageUrl?: string;
  minBillAmount?: number | null;
  maxPerBill?: number | null;
  allowStacking: boolean;
  branchIds: string[];
  validFromHour?: number | null;
  validToHour?: number | null;
  validDaysOfWeek: Weekday[];
  excludedDates: Dayjs[];
  fixedExpiresAt?: Dayjs | null;
}

interface Props {
  reward: Reward | null;
  isCreating: boolean;
  chains: Chain[];
  branches: Branch[];
  /** `Voucher:MaxPerBill` đang chạy — đọc từ `GET /admin/configs`, không hardcode 1. */
  systemMaxPerBill: number | undefined;
  submitting: boolean;
  /**
   * Tỷ giá định giá quà từ `Rewards:VndPerPoint` — một điểm đáng bao nhiêu đồng khi đổi quà.
   *
   * Chỉ để GỢI Ý và CẢNH BÁO. Giá điểm thật là `pointsCost` người khai nhập; server không đọc tỷ
   * giá này lúc đổi quà, vì một lần sửa số không được định giá lại danh mục về quá khứ (BR-105).
   *
   * `null` khi chưa tải được config — lúc đó ẩn hẳn phần gợi ý thay vì đoán một con số.
   */
  vndPerPoint: number | null;
  onSubmit: (payload: UpsertRewardPayload) => void;
  onClose: () => void;
}

function toFormConditions(c: VoucherConditions): Partial<FormValues> {
  return {
    minBillAmount: c.minBillAmount ?? undefined,
    maxPerBill: c.maxPerBill ?? undefined,
    allowStacking: c.allowStacking ?? false,
    branchIds: c.branchIds,
    validFromHour: c.validFromHour ?? undefined,
    validToHour: c.validToHour ?? undefined,
    validDaysOfWeek: c.validDaysOfWeek,
    excludedDates: c.excludedDates.map((d) => dayjs(d)),
    fixedExpiresAt: c.fixedExpiresAt ? dayjs(c.fixedExpiresAt) : undefined,
  };
}

function buildPayload(values: FormValues, chains: Chain[]): UpsertRewardPayload {
  const kind = values.kind;
  const group = rewardGroupOf(kind);
  const conditions: VoucherConditions = {
    minBillAmount: values.minBillAmount ?? null,
    maxPerBill: values.maxPerBill ?? null,
    allowStacking: values.allowStacking ?? false,
    branchIds: values.branchIds,
    validFromHour: values.validFromHour ?? null,
    validToHour: values.validToHour ?? null,
    validDaysOfWeek: values.validDaysOfWeek,
    excludedDates: values.excludedDates.map((d) => d.format('YYYY-MM-DD')),
    fixedExpiresAt: values.fixedExpiresAt?.toISOString() ?? null,
  };

  return {
    name: values.name.trim(),
    description: values.description?.trim() || null,
    imageUrl: values.imageUrl?.trim() || null,
    pointsCost: values.pointsCost,
    kind,
    // Mỗi loại chỉ gửi trường giá trị của riêng nó; các trường còn lại về null để không sót số cũ.
    discountAmount: kind === 'Discount' ? values.discountAmount ?? null : null,
    discountPercent: kind === 'PercentDiscount' ? values.discountPercent ?? null : null,
    maxDiscountAmount: kind === 'PercentDiscount' ? values.maxDiscountAmount ?? null : null,
    itemValue: group === 'InKind' ? values.itemValue ?? null : null,
    validDays: values.validDays,
    // Tồn kho chỉ nhóm tiền mới có; nhóm hiện vật để bếp quyết định tại quầy (BE cũng từ chối).
    stockRemaining: group === 'Money' ? values.stockRemaining ?? null : null,
    chainId: values.chainId ?? null,
    // LUÔN gửi mảng: bỏ trường đi nghĩa là "không đụng", và mã của chuỗi vừa bị bỏ khỏi
    // phạm vi sẽ nằm lại, hiện ra ở quầy cho thu ngân gõ.
    kasPromoCodes: toKasPayload(values.kasPromoCodes, values.chainId, chains),
    isActive: values.isActive,
    conditions,
    maxRedemptionsPerMember: values.maxRedemptionsPerMember ?? null,
    redemptionWindow: values.redemptionWindow,
    saleFrom: values.saleFrom?.toISOString() ?? null,
    saleUntil: values.saleUntil?.toISOString() ?? null,
  };
}

export function RewardFormModal({
  reward,
  isCreating,
  chains,
  branches,
  systemMaxPerBill,
  submitting,
  vndPerPoint,
  onSubmit,
  onClose,
}: Props) {
  const [form] = Form.useForm<FormValues>();
  const [current, setCurrent] = useState(0);
  const open = reward !== null || isCreating;
  const kind = Form.useWatch('kind', form) as RewardKind | undefined;
  const chainId = Form.useWatch('chainId', form) as string | null | undefined;
  const allowStacking = Form.useWatch('allowStacking', form) ?? false;
  const maxPerBill = Form.useWatch('maxPerBill', form) as number | null | undefined;
  const group = kind ? rewardGroupOf(kind) : undefined;
  const stackingNoOp = stackingIsIneffective(allowStacking, maxPerBill, systemMaxPerBill);

  // ── Gợi ý giá điểm theo tỷ giá `Rewards:VndPerPoint` ────────────────────────────────────
  //
  // Trước 2026-09-07 tỷ giá đổi quà KHÔNG tồn tại ở đâu trong hệ thống: người khai nhập `Giá
  // điểm` và `Mệnh giá` thành hai số rời nhau, và không tầng nào so hai số đó với nhau — nên gõ
  // 50 điểm cho voucher 100.000đ (tỷ giá 2.000đ, lệch 10 lần) đi qua im lặng.
  //
  // 📌 CẢNH BÁO, không chặn. Tỷ giá là quy ước định giá, không phải quy tắc nghiệp vụ: bán lỗ một
  // món chủ lực để kéo khách là quyết định hợp lệ. Khác vế "không nhận đặt 100% bàn" của BR-602 —
  // cái đó là quy tắc nên phải chặn.
  const pointsCost = Form.useWatch('pointsCost', form) as number | undefined;
  const discountAmount = Form.useWatch('discountAmount', form) as number | undefined;
  const maxDiscountAmount = Form.useWatch('maxDiscountAmount', form) as number | undefined;
  const itemValue = Form.useWatch('itemValue', form) as number | undefined;

  // Mệnh giá dùng để đối chiếu, theo từng loại quà. `PercentDiscount` lấy TRẦN tiền — đó là mức
  // tiền tối đa có thể đi ra từ voucher đó, tức phần rủi ro thật.
  const faceValue =
    kind === 'Discount' ? discountAmount
    : kind === 'PercentDiscount' ? maxDiscountAmount
    : group === 'InKind' ? itemValue
    : undefined;

  const suggestedPoints =
    vndPerPoint && faceValue ? Math.max(1, Math.round(faceValue / vndPerPoint)) : null;

  // Tỷ giá thực tế người khai đang đặt ra.
  const impliedRate = faceValue && pointsCost ? faceValue / pointsCost : null;

  // Lệch quá 20% thì cảnh báo. Dưới mức đó là làm tròn và chênh lệch bình thường của việc định giá.
  const rateOff =
    impliedRate && vndPerPoint ? Math.abs(impliedRate - vndPerPoint) / vndPerPoint > 0.2 : false;

  const branchOptions = branches
    .filter((b) => !chainId || b.chainId === chainId)
    .map((b) => ({ value: b.id, label: b.name }));

  useEffect(() => {
    if (!open) return;

    // Mở modal thì luôn về bước đầu — mở lại quà khác mà đang ở bước 3 là mất phương hướng.
    setCurrent(0);

    if (reward) {
      form.setFieldsValue({
        name: reward.name,
        description: reward.description ?? undefined,
        kind: reward.kind,
        pointsCost: reward.pointsCost,
        discountAmount: reward.discountAmount ?? undefined,
        discountPercent: reward.discountPercent ?? undefined,
        maxDiscountAmount: reward.maxDiscountAmount ?? undefined,
        itemValue: reward.itemValue ?? undefined,
        validDays: reward.validDays,
        stockRemaining: reward.stockRemaining,
        chainId: reward.chainId,
        kasPromoCodes: toKasFormValue(reward.kasPromoCodes),
        isActive: reward.isActive,
        maxRedemptionsPerMember: reward.maxRedemptionsPerMember ?? 3,
        redemptionWindow: reward.redemptionWindow,
        saleFrom: reward.saleFrom ? dayjs(reward.saleFrom) : undefined,
        saleUntil: reward.saleUntil ? dayjs(reward.saleUntil) : undefined,
        imageUrl: reward.imageUrl ?? undefined,
        allowStacking: false,
        branchIds: [],
        validDaysOfWeek: [],
        excludedDates: [],
        ...toFormConditions(reward.conditions),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        kind: 'Discount',
        validDays: 30,
        isActive: true,
        maxRedemptionsPerMember: 3,
        redemptionWindow: 'Month',
        allowStacking: false,
        branchIds: [],
        validDaysOfWeek: [],
        excludedDates: [],
      });
    }
  }, [reward, open, form]);

  // Tên field cần validate khi bấm "Tiếp" — chỉ chặn ở bước có ràng buộc, các bước điều kiện để trống được.
  const namesForStep = (s: number): string[] => {
    if (s === 0) return ['name', 'kind', 'pointsCost', 'validDays'];
    if (s === 1) {
      if (kind === 'Discount') return ['discountAmount'];
      if (kind === 'PercentDiscount') return ['discountPercent', 'maxDiscountAmount'];
      return ['itemValue'];
    }
    return [];
  };

  const goNext = () => {
    form
      .validateFields(namesForStep(current))
      .then(() => setCurrent((c) => c + 1))
      // Lỗi đã hiện ngay dưới field của bước hiện tại — không nhảy bước.
      .catch(() => {});
  };

  const goSubmit = () => {
    form
      .validateFields()
      .then((values) => onSubmit(buildPayload(values, chains)))
      .catch(() => {});
  };

  const STEP_TITLES = ['Cơ bản', 'Giá trị', 'Điều kiện', 'Thời gian'];
  const lastStep = STEP_TITLES.length - 1;
  const showAt = (index: number) => ({ display: current === index ? 'block' : 'none' });

  return (
    <Modal
      open={open}
      title={isCreating ? 'Thêm quà' : `Sửa ${reward?.name ?? ''}`}
      width={640}
      destroyOnHidden
      onCancel={onClose}
      footer={[
        current > 0 ? (
          <Button key="back" onClick={() => setCurrent((c) => c - 1)} disabled={submitting}>
            Quay lại
          </Button>
        ) : null,
        <Button key="cancel" onClick={onClose} disabled={submitting}>
          Hủy
        </Button>,
        current < lastStep ? (
          <Button key="next" type="primary" onClick={goNext}>
            Tiếp
          </Button>
        ) : (
          <Button key="ok" type="primary" loading={submitting} onClick={goSubmit}>
            Lưu
          </Button>
        ),
      ]}
    >
      <Steps
        size="small"
        current={current}
        items={STEP_TITLES.map((title) => ({ title }))}
        style={{ marginBottom: 24 }}
      />
      {/* Mọi bước luôn được mount (chỉ ẩn/hiện bằng display) để giá trị và điều kiện hiện-theo-loại
          không bị mất khi chuyển bước, và validate cuối vẫn quét đủ field. */}
      <Form form={form} layout="vertical" requiredMark={false}>
        {/* Bước 1 — Cơ bản */}
        <div style={showAt(0)}>
          <Form.Item name="name" label="Tên quà" rules={[{ required: true, message: 'Chưa nhập tên quà.' }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Form.Item
            name="kind"
            label="Loại quà"
            rules={[{ required: true }]}
            tooltip="Voucher tiền và Giảm % thuộc nhóm tiền; món ăn, mua 1 tặng 1, combo thuộc nhóm hiện vật. Nhóm quyết định trần 50% đọc mệnh giá ở đâu và loại nào được khai tồn kho — không quyết định hạn mức mỗi hóa đơn."
          >
            <Select options={KIND_OPTIONS} />
          </Form.Item>
          <Form.Item name="pointsCost" label="Giá điểm" rules={[{ required: true, message: 'Chưa nhập giá điểm.' }]}>
            <InputNumber min={1} style={{ width: '100%' }} formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
          </Form.Item>
          <Form.Item
            name="validDays"
            label="Số ngày hiệu lực sau khi đổi"
            rules={[{ required: true }]}
            tooltip="1–730 ngày, trừ khi đặt hạn cố định ở bước Thời gian."
          >
            <InputNumber min={1} max={730} style={{ width: '100%' }} />
          </Form.Item>
          {group === 'Money' ? (
            <Form.Item name="stockRemaining" label="Tồn kho">
              <InputNumber min={0} placeholder="Để trống = không giới hạn" style={{ width: '100%' }} formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
            </Form.Item>
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Quà hiện vật không có tồn kho hệ thống"
              description="Bếp mới biết mình còn gì — nhân viên từ chối tại quầy nếu hết món. Cần giới hạn thì giới hạn phạm vi chi nhánh ở bước Thời gian."
            />
          )}
          <Form.Item name="chainId" label="Phạm vi chuỗi">
            <Select allowClear placeholder="Toàn hệ thống" options={chains.map((c) => ({ value: c.id, label: c.name }))} />
          </Form.Item>
          {/* Đặt ngay dưới Phạm vi chuỗi: bộ ô đổi theo phạm vi, để cạnh nhau thì người khai thấy
              được quan hệ đó thay vì phải nhớ. */}
          <Form.Item name="kasPromoCodes" noStyle>
            <KasPromoCodeFields chains={chains} chainId={chainId} />
          </Form.Item>
          <Form.Item name="isActive" label="Đang bán" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="imageUrl" label="Ảnh">
            <ImageUploadField />
          </Form.Item>
        </div>

        {/* Bước 2 — Giá trị (hiện theo Loại đã chọn) */}
        <div style={showAt(1)}>
          {kind === 'Discount' && (
            <Form.Item name="discountAmount" label="Mệnh giá (đồng)" rules={[{ required: true, message: 'Voucher tiền cần mệnh giá.' }]}>
              <InputNumber min={1} step={10_000} style={{ width: '100%' }} formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
            </Form.Item>
          )}
          {kind === 'PercentDiscount' && (
            <>
              <Form.Item
                name="discountPercent"
                label="Tỷ lệ giảm (%)"
                rules={[
                  { required: true, message: 'Voucher giảm % cần tỷ lệ.' },
                  { type: 'number', min: 1, max: 100, message: 'Tỷ lệ trong khoảng 1–100.' },
                ]}
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} addonAfter="%" />
              </Form.Item>
              <Form.Item
                name="maxDiscountAmount"
                label="Trần số tiền giảm (đồng)"
                rules={[{ required: true, message: 'Giảm % bắt buộc có trần tiền.' }]}
                tooltip="Bắt buộc — 20% của hóa đơn tiệc 50 triệu là 10 triệu đi ra từ một voucher vài trăm điểm nếu không có trần."
              >
                <InputNumber min={1} step={10_000} style={{ width: '100%' }} formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
              </Form.Item>
            </>
          )}
          {group === 'InKind' && (
            <Form.Item
              name="itemValue"
              label="Giá trị quy đổi (đồng)"
              tooltip="KHÔNG hiện cho khách — chỉ để trần khuyến mại 50% hóa đơn đếm được món tặng này. Để trống thì trần không thấy giá trị quà. Với Mua 1 tặng 1, nhập mức CAO NHẤT có thể chứ không phải trung bình."
            >
              <InputNumber min={1} step={10_000} style={{ width: '100%' }} placeholder="Để trống = không đếm vào trần" formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
            </Form.Item>
          )}

          {suggestedPoints !== null && vndPerPoint !== null && (
            <Alert
              type={rateOff ? 'warning' : 'info'}
              showIcon
              style={{ marginTop: 8 }}
              message={
                rateOff
                  ? `Giá điểm lệch xa tỷ giá đang dùng (1 điểm = ${nhomNghin.formatter(vndPerPoint)}đ)`
                  : `Tỷ giá đang dùng: 1 điểm = ${nhomNghin.formatter(vndPerPoint)}đ`
              }
              description={
                <>
                  Mệnh giá {nhomNghin.formatter(faceValue!)}đ theo tỷ giá này là{' '}
                  <b>{nhomNghin.formatter(suggestedPoints)} điểm</b>
                  {impliedRate !== null && pointsCost ? (
                    <>
                      {' '}— đang khai <b>{nhomNghin.formatter(pointsCost)} điểm</b>, tức{' '}
                      {nhomNghin.formatter(Math.round(impliedRate))}đ mỗi điểm.
                    </>
                  ) : (
                    '.'
                  )}
                  {rateOff && (
                    <>
                      {' '}Định giá lệch có chủ đích thì giữ nguyên — đây là cảnh báo, không phải
                      phép chặn.
                    </>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <Button
                      size="small"
                      onClick={() => form.setFieldsValue({ pointsCost: suggestedPoints })}
                      disabled={pointsCost === suggestedPoints}
                    >
                      Đặt {nhomNghin.formatter(suggestedPoints)} điểm
                    </Button>
                  </div>
                </>
              }
            />
          )}
        </div>

        {/* Bước 3 — Điều kiện áp dụng và cộng dồn */}
        <div style={showAt(2)}>
          <Form.Item name="minBillAmount" label="Hóa đơn tối thiểu (đồng)">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Để trống = theo hệ thống" formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
          </Form.Item>
          <Form.Item
            name="maxPerBill"
            label="Tối đa trên một hóa đơn"
            extra="Để trống thì dùng quy tắc chung hệ thống. Đếm tổng voucher trên hóa đơn, không chia loại hay nhóm."
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder={
                systemMaxPerBill != null
                  ? `Để trống = theo hệ thống (${systemMaxPerBill})`
                  : 'Để trống = theo hệ thống'
              }
            />
          </Form.Item>
          <Form.Item name="maxRedemptionsPerMember" label="Giới hạn đổi / thành viên">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="redemptionWindow" label="Chu kỳ giới hạn đổi">
            <Select options={Object.entries(REDEMPTION_WINDOW_LABELS).map(([value, label]) => ({ value, label }))} />
          </Form.Item>
          <Form.Item
            name="allowStacking"
            label="Cho phép dùng chung với ưu đãi khác"
            valuePropName="checked"
            tooltip="Mặc định tắt: một hóa đơn một ưu đãi. Phải bật ở CẢ HAI món quà mới có tác dụng — và chỉ chạy khi 'Tối đa trên một hóa đơn' ≥ 2. Chiết khấu hạng thẻ không phải ưu đãi nên luôn đi kèm."
          >
            <Switch />
          </Form.Item>
          {stackingNoOp && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Cờ này chưa có tác dụng"
              description="Trần mỗi hóa đơn đang là 1 voucher — voucher thứ hai bị chặn trước khi hệ thống hỏi dùng chung. Nới “Tối đa trên một hóa đơn” lên từ 2 (ô này hoặc quy tắc chung hệ thống). Vẫn lưu được: có thể khai trước rồi nới trần sau."
            />
          )}
        </div>

        {/* Bước 4 — Điều kiện thời gian và cửa sổ bán */}
        <div style={showAt(3)}>
          <Form.Item name="branchIds" label="Chi nhánh được dùng">
            <Select mode="multiple" allowClear options={branchOptions} placeholder="Mọi chi nhánh trong chuỗi" />
          </Form.Item>
          <Form.Item
            label="Khung giờ (giờ Việt Nam)"
            extra="Muốn chạy xuyên đêm thì tách hai khung: 22–23 và 0–2. Giờ kết thúc không tính vào khung."
          >
            <Input.Group compact style={{ display: 'flex' }}>
              <Form.Item name="validFromHour" noStyle>
                <InputNumber min={0} max={23} placeholder="Từ" style={{ width: '50%' }} />
              </Form.Item>
              <Form.Item
                name="validToHour"
                dependencies={['validFromHour']}
                style={{ width: '50%', marginBottom: 0 }}
                rules={[
                  {
                    validator: (_, value: number | null | undefined) => {
                      const from = form.getFieldValue('validFromHour') as number | null | undefined;
                      if (value == null || from == null) return Promise.resolve();
                      if (value <= from) {
                        return Promise.reject(
                          new Error('Giờ kết thúc phải sau giờ bắt đầu trong cùng ngày.'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber min={0} max={23} placeholder="Đến" style={{ width: '100%' }} />
              </Form.Item>
            </Input.Group>
          </Form.Item>
          <Form.Item name="validDaysOfWeek" label="Thứ trong tuần">
            <Select mode="multiple" allowClear options={WEEKDAY_OPTIONS} placeholder="Mọi ngày" />
          </Form.Item>
          <Form.Item name="excludedDates" label="Ngày loại trừ">
            <DatePicker multiple style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="fixedExpiresAt" label="Hạn dùng cố định">
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="saleFrom" label="Bán từ">
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="saleUntil" label="Bán đến">
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
